<template>
    <div class="col-sm-12 nopads">
        <!-- Area report wizard -->
        <Transition name="fade">

            <form v-if="areaReportWizardMode" class="col-12 editor-form pt-1">
                <div class="col-12 form-data">
                    <span class="span-title">{{ $t('area_report.select_area') }}</span>
                    <div class="form-group title">
                        <select
                            id="area"
                            v-model="selectedArea"
                            class="form-control form-control-sm"
                            :disabled="nearbyAreas.length < 1"
                        >
                            <option :value="null">{{ $t('area_report.select_area') }}</option>
                            <option v-for="area in nearbyAreas" :key="area.id" :value="area.id">
                                {{ area.name }}
                            </option>
                        </select>
                    </div>

                    <span v-if="!userPosition" class="hint-text">
                        {{ $t('common.fetching_location') }}
                    </span>
                    <span v-else-if="nearbyAreas.length === 0" class="hint-text">
                        {{ $t('area_report.location_hint') }}
                    </span>

                    <div class="col-12 button-container">
                        <button type="button" class="btn btn-secondary form-button" @click.stop="$emit('close')">
                            {{ $t('common.close') }}
                        </button>
                        <button type="button" class="btn btn-success form-button" :disabled="loading || selectedArea === null" @click.stop="addAreaReport">
                            {{ $t('common.next') }}
                        </button>
                    </div>
                </div>
            </form>
        </transition>


        <Transition name="fade">
            <div v-if="equipmentReportMode && areaReportUpdate && areaReportUpdate.area" class="nopads">
                <div class="col-12 nopads" v-for="equipment in areaReportUpdate.area.equipment" :key="equipment.id">
                    <Transition name="fade">
                        <area-equipment-report-editor
                            v-if="!isInspected(equipment.id)"
                            :equipment="equipment"
                            :report-id="areaReportUpdate.id"
                            @result-uploaded="equipmentInspected"
                        />
                    </Transition>
                </div>
            </div>
        </Transition>

        <!--    Area report editor-->
        <div
            v-if="!equipmentReportMode && !areaReportWizardMode"
            class="nopads"
        >
            <!-- Area report -->
            <form class="col-12 editor-form nopads">
                <h2 class="editor-header" style="font-size: 1.2em">{{ $t('area_report_details.area_inspection') }}</h2>
                <div class="col-12 form-data nopads">
                    <span class="span-title">{{ $t('area_report.area_name') }}</span>
                    <div class="form-group title">
                        <label for="area"><strong>{{ areaReportUpdate.area.name }}</strong></label>
                    </div>

                    <span class="span-title">{{ $t('area_report.note') }}</span>
                    <div class="form-group title">
                        <label for="note">{{ $t('area_report.note') }}</label>
                        <textarea
                            id="note"
                            v-model="note"
                            class="form-control"
                            rows="4"
                        ></textarea>
                    </div>

                    <div class="col-12 button-container">
                        <button type="button" class="btn btn-secondary form-button" @click.stop="$emit('close')">
                            {{ $t('common.cancel') }}
                        </button>
                        <button type="button" class="btn btn-success" :disabled="loading" @click.stop="submitAreaReport">
                            {{ $t('common.save') }}
                        </button>
                    </div>
                </div>
            </form>
        </div>

        <!-- Loading Spinner -->
        <div v-if="loading" id="loader" class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
        </div>
    </div>
</template>

<script>
import {restApi} from '../../mixins/RestApiMixin'
import {areaReportHelper} from '../../mixins/AreaReportMixin.js'
import AreaEquipmentReportEditor from '../areaequipment/AreaEquipmentReportEditor'
import {isPointInPolygon} from "geolib";
import {inject} from "vue";

export default {
    name: 'AreaReportEditor',
    components: {AreaEquipmentReportEditor},
    mixins: [areaReportHelper, restApi],
    props: {
        areaReport: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            note: '',
            equipmentReportMode: false,
            areaReportWizardMode: true,
            userPosition: null,
            selectedArea: null,
            areas: [],
            nearbyAreas: [],
            inspectionResults: [],
            areaReportUpdate: this.areaReport ? this.copyReport(this.areaReport) : null,
            watchID: null
        }
    },

    created() {
        this.fetchAreas()
    },

    mounted() {
        if (this.areaReport) {
            this.editEquipmentReport()
        }
        this.initPosition()
        this.watchUserPosition()
    },

    beforeUnmount() {
        if (this.watchID) {
            navigator.geolocation.clearWatch(this.watchID);
        }
    },

    methods: {
        initPosition: function () {
            navigator.geolocation.getCurrentPosition(pos =>  this.setPosition(pos))
        },

        equipmentInspected(equipmentResult) {
            this.inspectionResults.push(equipmentResult)
            if (this.inspectionResults.length === this.areaReportUpdate.area.equipment.length) {
                this.areaReportWizardMode = false
                this.equipmentReportMode = false
            }
        },

        isInspected(equipmentId) {
           return this.inspectionResults.find(item => item.equipment.id === equipmentId)
        },

        fetchAreas: function () {
            this.loading = true
            this.restFetchParams(this.areaUrl, {equipment: true}, this.handleAreaResponse)
        },

        handleAreaResponse: function (response) {
            this.loading = false
            this.areas = response.data
            this.filterNearbyAreas()
        },

        filterNearbyAreas: function () {
            this.nearbyAreas = []
            if (this.userPosition) {
                this.areas.forEach(area => {
                    if (isPointInPolygon(
                        {latitude: this.userPosition.coords.latitude, longitude: this.userPosition.coords.longitude},
                        this.getAreaPolygon(area))) {
                        this.nearbyAreas.push(area)
                    }
                }, this)
            }
        },

        watchUserPosition: function () {
            if (navigator.geolocation) {
                this.watchID = navigator.geolocation.watchPosition(position => {
                    this.setPosition(position)
                }, () => {
                }, {timeout: 30000, enableHighAccuracy: true})
            }
        },

        setPosition: function (position) {
            this.userPosition = position
            this.filterNearbyAreas()
        },
        submitAreaReport: function () {
            if (this.areaReportUpdate !== undefined && this.areaReportUpdate !== null) {
                this.loading = true
                this.restAdd(this.areaReportSubmitUrl, {
                    'report_id': this.areaReportUpdate.id,
                    'note': this.note
                }, this.submitAreaReportSuccess, this.submitAreaReportFail)
            }
        },

        submitAreaReportSuccess: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },

        submitAreaReportFail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },

        editAreaReport: function (areaReport) {
            this.areaReportUpdate = areaReport
            this.equipmentReportMode = false
        },

        addAreaReport: function () {
            let area = this.areas.find(area => area.id === this.selectedArea)
            this.areaReportUpdate = this.newAreaReport()
            this.areaReportUpdate.area = {id: area.id}
            this.loading = true
            this.restAdd(this.areaReportUrl, this.areaReportUpdate, this.addAreaSuccess, this.addAreaFail)
        },

        addAreaSuccess: function (responseData) {
            this.areaReportUpdate = responseData
            this.loading = false
            this.editEquipmentReport()
        },

        addAreaFail: function () {
            this.showAlert(this.$t('common.alert_add_failed'))
            this.loading = false
        },

        editEquipmentReport: function () {
            this.areaReportWizardMode = false
            this.equipmentReportMode = true
        }
    }
}
</script>
